import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TurfTalk, AuthExpiredError } from "@/api/client";
import { isLiveStatus, orderTickerGames, classifyGame, combineHomeGames, pollIntervalForGames } from "@/lib/gameStatus";
import { loadTickerOdds, tickerSpreadFor } from "@/lib/tickerOdds";

// Page-session odds cache, keyed by game id. Persists across re-renders/remounts
// so the ticker never refetches the same future game.
const ODDS_CACHE = new Map();

function kickoff(iso) {
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function TickerItem({ g, ariaHidden, spread }) {
  const live = isLiveStatus(g.status);
  const final = g.status === "final";
  const showScore = live || final;
  const statusText = live ? `${g.period ? `Q${g.period} ` : ""}${g.clock || "LIVE"}`.trim() : final ? "FINAL" : kickoff(g.start_time);
  return (
    <Link to={`/game/${g.id}`} {...(ariaHidden ? { "aria-hidden": true, tabIndex: -1 } : { "data-testid": `ticker-game-${g.id}` })}
      className="flex items-center gap-2.5 px-3.5 h-full border-r border-[#2A3544] hover:bg-white/5 transition-colors shrink-0">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${live ? "bg-[#D92D20] tt-live-dot" : final ? "bg-white/25" : "bg-[#F4B740]"}`} />
      <span className="text-[10px] font-display tracking-wider text-white/35">{g.league}</span>
      <span className="flex items-center gap-1.5 text-xs tabnums">
        <span className="text-white/80">{g.away?.abbr || "—"}</span>
        {showScore && <span className="font-display text-white">{g.away?.score ?? 0}</span>}
        <span className="text-white/25">{showScore ? ":" : "@"}</span>
        {showScore && <span className="font-display text-white">{g.home?.score ?? 0}</span>}
        <span className="text-white/80">{g.home?.abbr || "—"}</span>
      </span>
      {spread && !showScore && <span {...(ariaHidden ? {} : { "data-testid": `ticker-odds-${g.id}` })} className="text-[10px] tabnums font-display tracking-wide text-white/55">{spread}</span>}
      {statusText && <span className={`text-[10px] tabnums font-display tracking-wide ${live ? "text-[#ff6b60]" : final ? "text-white/40" : "text-[#F4B740]"}`}>{statusText}</span>}
    </Link>
  );
}

export function ScoreTicker() {
  const [games, setGames] = useState(null); // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    TurfTalk.home()
      .then((h) => { if (mounted) setGames(combineHomeGames(h)); })
      .catch((e) => { if (mounted && !(e instanceof AuthExpiredError)) setError(true); });
    return () => { mounted = false; };
  }, []);

  // Shared polling policy: fast (45s) while any game is active, else slow (5m).
  // Single interval; resets only when the cadence actually changes.
  const anyLive = useMemo(() => (games || []).some((g) => isLiveStatus(g.status)), [games]);
  const intervalMs = useMemo(() => pollIntervalForGames(games || []), [games]);
  useEffect(() => {
    const iv = setInterval(() => { TurfTalk.home().then((h) => setGames(combineHomeGames(h))).catch(() => {}); }, intervalMs);
    return () => clearInterval(iv);
  }, [intervalMs]);

  const sorted = useMemo(() => orderTickerGames(games), [games]);

  // Bounded odds load for the ticker's genuine-future slice only. Cached by id
  // for the page session; keyed effect avoids refetching on unrelated re-renders.
  const [oddsVersion, setOddsVersion] = useState(0);
  const upcoming = useMemo(() => sorted.filter((g) => classifyGame(g) === "upcoming"), [sorted]);
  const upcomingKey = upcoming.map((g) => g.id).join(",");
  useEffect(() => {
    if (!upcoming.length) return;
    let active = true;
    loadTickerOdds(upcoming, (id) => TurfTalk.odds(id), ODDS_CACHE)
      .then(() => { if (active) setOddsVersion((v) => v + 1); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingKey]);
  const oddsMap = useMemo(() => Object.fromEntries(ODDS_CACHE), [oddsVersion]);

  const wrap = "h-11 md:h-12 border-b border-[#2A3544] bg-[#0d1219]";
  if (games === null && !error) {
    return <div data-testid="score-ticker" className={`${wrap} flex items-center px-4 text-xs text-white/40`}>Loading scores…</div>;
  }
  if (error || sorted.length === 0) {
    return (
      <div data-testid="score-ticker" className={`${wrap} flex items-center px-4 text-xs text-white/40`}>
        {error ? "Live scores are being connected." : "No games in the current feed yet."}
      </div>
    );
  }
  // Continuous marquee: duplicate the list so translateX(-50%) loops seamlessly.
  // Speed scales with the number of games for a steady, readable pace.
  const durationSec = Math.max(30, sorted.length * 4);
  return (
    <div data-testid="score-ticker" className={`${wrap} flex items-stretch overflow-hidden`}>
      <div className="flex items-center px-3 shrink-0 border-r border-[#2A3544] bg-[#0d1219] z-10">
        <span className="text-[10px] font-display tracking-[0.2em] text-[#4ea152]">SCORES</span>
        {anyLive && <span className="ml-2 w-1.5 h-1.5 rounded-full bg-[#D92D20] tt-live-dot" data-testid="ticker-live-dot" />}
      </div>
      <div className="tt-ticker-viewport relative flex-1 min-w-0 overflow-hidden">
        <div className="tt-ticker-track" style={{ "--tt-ticker-dur": `${durationSec}s` }}>
          {sorted.map((g) => <TickerItem key={`a-${g.id}`} g={g} spread={tickerSpreadFor(g, oddsMap)} />)}
          {sorted.map((g) => <TickerItem key={`b-${g.id}`} g={g} spread={tickerSpreadFor(g, oddsMap)} ariaHidden />)}
        </div>
      </div>
    </div>
  );
}
